import React, { useState } from 'react';
import { gql, useSuspenseQuery } from '@apollo/client';
import { Checkbox } from '@/components/shadcn/checkbox';
import {
  OrgEventsMetadataQueryQuery,
  OrgEventsMetadataSignificantDateSource,
} from '@/__generated__/graphql';
import SPECIAL_CALENDARS from '@/data/special-calendars.json';
import SettingsTabTitle from '../settings-tab-title';
import { useOrganizationContext } from '@/contexts/organization-context';
import { logError } from '@/lib/rum';

interface ReligiousCalendarsProps {
  onChange: (
    id: string,
    values: OrgEventsMetadataSignificantDateSource[]
  ) => void;
}

const OrgEventsMetadataQuery = gql(`
  query OrgEventsMetadataQuery(
    $orgEventsInput: OrgEventsMetadataGetOneInput!
  ) {
    orgEventsMetadata {
      getOne(input: $orgEventsInput) {
        ... on OrgEventsMetadataGetOneSuccess {
          result {
            id
            significantDateSources {
              key
              orgNameFilter
            }
          }
        }
      }
    }
  }
  `);

const ReligiousCalendars = ({ onChange }: ReligiousCalendarsProps) => {
  const { organizationId } = useOrganizationContext();
  const { data: orgMetadata, error } =
    useSuspenseQuery<OrgEventsMetadataQueryQuery>(OrgEventsMetadataQuery, {
      variables: {
        orgEventsInput: {
          where: {
            organizationId: organizationId,
          },
        },
      },
    });

  const initialSelected =
    orgMetadata?.orgEventsMetadata?.getOne.__typename ===
    'OrgEventsMetadataGetOneSuccess'
      ? orgMetadata.orgEventsMetadata.getOne.result.significantDateSources.map(
          (source: OrgEventsMetadataSignificantDateSource) => source.key
        )
      : [];

  const [selected, setSelected] = useState<any>(initialSelected);

  if (!orgMetadata) {
    return null;
  }

  const onSelected = (value: boolean, key: string) => {
    let newSelected = [...selected];
    if (value) {
      newSelected.push(key);
    } else {
      newSelected = newSelected.filter((source) => source !== key);
    }

    setSelected(newSelected);

    if (
      orgMetadata?.orgEventsMetadata?.getOne.__typename ===
      'OrgEventsMetadataGetOneSuccess'
    ) {
      const holidays = newSelected.map((holiday) => ({
        key: holiday,
      }));

      onChange(orgMetadata.orgEventsMetadata.getOne.result.id, holidays);
    }
  };

  if (error) {
    logError(
      'Something went wrong while loading organization grant settings',
      error
    );
  }

  return (
    <div className='mt-8'>
      <SettingsTabTitle title='Religious Calendars' />
      <div className='-mt-2 grid grid-cols-2'>
        {SPECIAL_CALENDARS.map((item, index) => (
          <div key={index} className='mb-2 flex gap-2'>
            <Checkbox
              variant={'maggie'}
              onCheckedChange={(isChecked: boolean) =>
                onSelected(isChecked, item.key)
              }
              checked={!!selected?.includes(item.key)}
            />
            {item.name}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReligiousCalendars;
